import { useState, useCallback, useEffect, useRef } from "react";
import { Loader2, Wand2, FileText, Film } from "lucide-react";
import VideoTypePicker, { type VideoType } from "./video-type-picker";
import VideoForm, { type VideoFormInputs } from "./video-form";
import VideoResult from "./video-result";
import { apiRequest, getAuthToken } from "@/lib/queryClient";

type GeneratorStep = "pick-type" | "form" | "processing" | "done";

interface JobStatusResponse {
  jobId: string;
  status: "queued" | "generating-script" | "rendering" | "done" | "error";
  progress: number;
  script: unknown;
  outputPath: string | null;
  error: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  queued: "W kolejce...",
  "generating-script": "Generuję scenariusz AI...",
  rendering: "Renderuję wideo...",
  done: "Gotowe!",
  error: "Błąd",
};

export default function VideoGenerator() {
  const [step, setStep] = useState<GeneratorStep>("pick-type");
  const [selectedType, setSelectedType] = useState<VideoType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (id: string) => {
      try {
        const status: JobStatusResponse = await apiRequest(
          "GET",
          `/api/video-generator/status/${id}`
        );
        setJobStatus(status);

        if (status.status === "done") {
          stopPolling();
          setStep("done");
        }

        if (status.status === "error") {
          stopPolling();
          setError(status.error || "Nieznany błąd");
          setStep("form");
          setIsSubmitting(false);
        }
      } catch (err) {
        console.error("[VIDEO-GEN] Poll error:", err);
      }
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (id: string) => {
      stopPolling();
      pollingRef.current = setInterval(() => pollStatus(id), 2000);
      pollStatus(id);
    },
    [pollStatus, stopPolling]
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleTypeSelect = useCallback((type: VideoType) => {
    setSelectedType(type);
    setStep("form");
    setError(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (inputs: VideoFormInputs) => {
      if (!selectedType) return;
      setIsSubmitting(true);
      setError(null);

      try {
        const result = await apiRequest("POST", "/api/video-generator/create", {
          type: selectedType,
          inputs,
        });

        setJobId(result.jobId);
        setStep("processing");
        startPolling(result.jobId);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Błąd tworzenia zadania");
        setIsSubmitting(false);
      }
    },
    [selectedType, startPolling]
  );

  const handleReset = useCallback(() => {
    stopPolling();
    if (jobId) {
      apiRequest("DELETE", `/api/video-generator/job/${jobId}`).catch(() => {});
    }
    setStep("pick-type");
    setSelectedType(null);
    setJobId(null);
    setJobStatus(null);
    setError(null);
    setIsSubmitting(false);
  }, [jobId, stopPolling]);

  const previewUrl = jobId
    ? `/api/video-generator/preview/${jobId}?token=${getAuthToken() || ""}`
    : null;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto w-full p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-[#ef3e29]/60 flex items-center justify-center">
            <Wand2 size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Generator Wideo AI</h2>
            <p className="text-xs text-white/40">Wybierz typ → Opisz temat → AI wygeneruje film</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs">
          {(["pick-type", "form", "processing", "done"] as const).map((s, i) => {
            const labels = ["Typ", "Formularz", "Generowanie", "Gotowe"];
            const stepOrder = ["pick-type", "form", "processing", "done"] as const;
            const isActive = s === step;
            const isDone = stepOrder.indexOf(step) > stepOrder.indexOf(s);
            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <div className={`w-8 h-px ${isDone ? "bg-[#ef3e29]" : "bg-white/10"}`} />}
                <div
                  className={`
                  px-2.5 py-1 rounded-full transition
                  ${isActive ? "bg-[#ef3e29]/20 text-[#ef3e29]" : isDone ? "bg-green-500/20 text-green-400" : "bg-white/[0.04] text-white/30"}
                `}
                >
                  {labels[i]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Step: Pick Type */}
        {step === "pick-type" && <VideoTypePicker onSelect={handleTypeSelect} />}

        {/* Step: Form */}
        {step === "form" && selectedType && (
          <VideoForm
            type={selectedType}
            onSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
            onBack={() => {
              setStep("pick-type");
              setSelectedType(null);
              setIsSubmitting(false);
            }}
          />
        )}

        {/* Step: Processing */}
        {step === "processing" && jobStatus && (
          <div className="flex flex-col items-center gap-6 py-12">
            <Loader2 size={40} className="text-[#ef3e29] animate-spin" />

            <div className="text-center">
              <p className="text-white/70 text-sm font-medium">
                {STATUS_LABELS[jobStatus.status] || jobStatus.status}
              </p>
              <p className="text-white/30 text-xs mt-1">{jobStatus.progress}% ukończono</p>
            </div>

            {/* Progress bar */}
            <div className="w-full max-w-sm">
              <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#ef3e29] rounded-full transition-all duration-500"
                  style={{ width: `${jobStatus.progress}%` }}
                />
              </div>
            </div>

            {/* Status details */}
            <div className="flex items-center gap-4 text-xs text-white/30">
              <div className="flex items-center gap-1.5">
                <FileText size={12} />
                <span>Scen: {jobStatus.script ? "✓" : "..."}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Film size={12} />
                <span>
                  Render:{" "}
                  {jobStatus.status === "rendering"
                    ? `${jobStatus.progress}%`
                    : jobStatus.status === "done"
                      ? "✓"
                      : "..."}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === "done" && jobId && previewUrl && (
          <VideoResult jobId={jobId} previewUrl={previewUrl} onReset={handleReset} />
        )}
      </div>
    </div>
  );
}
